import React from 'react';
import { Plus, Minus, Check } from 'lucide-react';
import { fmtPct } from './format';

function ActionButton({ kind, recommended, selected, elsewhere, onClick, label }) {
  const Icon = selected || elsewhere ? Check : kind === 'include' ? Plus : Minus;
  const tone = elsewhere
    ? 'border-blue-200 bg-blue-50 text-blue-700'
    : kind === 'include' && (recommended || selected)
      ? 'border-green-700 bg-green-700 text-white'
      : kind === 'exclude' && (recommended || selected)
        ? 'border-red-700 bg-red-700 text-white'
        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-800';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-7 w-7 items-center justify-center rounded border ${tone}`}
      aria-label={label}
      title={label}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
    </button>
  );
}

/** "2.4×" for over-index, "0.4×" for under-index; colour by direction. */
function ratio(index) {
  if (index == null) return { text: 'n/a', tone: 'text-gray-400' };
  const r = index / 100;
  if (r === 0) return { text: 'none', tone: 'text-red-700' };
  const text = r >= 10 ? `${Math.round(r)}×` : r < 0.1 ? `${r.toFixed(2)}×` : `${r.toFixed(1)}×`;
  if (index >= 150) return { text, tone: 'text-green-700' };
  if (index <= 67) return { text, tone: 'text-red-700' };
  return { text, tone: 'text-gray-700' };
}

function MiniBars({ shareConv, shareBase, baselineLabel }) {
  const max = Math.max(1, shareConv || 0, shareBase || 0);
  const w = (v) => `${Math.max(2, Math.round((100 * (v || 0)) / max))}%`;
  return (
    <div className="space-y-1" aria-hidden>
      <div className="flex items-center gap-2 text-[11px] text-gray-600">
        <span className="w-16 shrink-0">converters</span>
        <div className="h-2 flex-1 rounded bg-gray-100 overflow-hidden"><div className="h-full rounded bg-[#0062FB]" style={{ width: w(shareConv) }} /></div>
        <span className="w-9 text-right tabular-nums text-gray-800">{fmtPct(shareConv)}</span>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <span className="w-16 shrink-0 truncate">{baselineLabel}</span>
        <div className="h-2 flex-1 rounded bg-gray-100 overflow-hidden"><div className="h-full rounded bg-gray-400/70" style={{ width: w(shareBase) }} /></div>
        <span className="w-9 text-right tabular-nums">{fmtPct(shareBase)}</span>
      </div>
    </div>
  );
}

/**
 * Ranked findings as compact cards: field:value is first, then the
 * ratio headline, with a two-bar strip of the shares.
 * The full sentence lives in the tooltip.
 */
export default function InsightList({ insights, loading, onAddRule, isRuleActive, isRuleElsewhere, activeDirection, onJumpToFeature, hasConverterData = true, baselineLabel = 'baseline' }) {
  if (loading) return <div className="text-sm text-gray-500">Loading insights…</div>;
  if (!insights?.length) {
    return (
      <div className="text-sm text-gray-500">
        {hasConverterData ? 'No significant findings for this baseline.' : 'No converter data for this offer, so nothing to compare yet.'}
      </div>
    );
  }
  return (
    <ol className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {insights.map((i) => {
        const active = isRuleActive?.(i);
        const elsewhere = !active && isRuleElsewhere?.(i);
        const direction = active || elsewhere ? activeDirection?.(i) : null;
        const r = ratio(i.index);
        const over = i.direction === 'over';
        const includeSelected = direction === 'over';
        const excludeSelected = direction === 'under';
        return (
          <li key={`${i.feature}::${i.value}`} className="rounded-lg border bg-white p-3 flex flex-col gap-2" title={i.text}>
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                className="min-w-0 text-sm font-medium text-gray-900 hover:underline truncate text-left"
                title={`${i.display_label}: ${i.value_label}`}
                onClick={() => onJumpToFeature?.(i)}
              >
                {i.display_label}: {i.value_label}
              </button>
              {onAddRule ? (
                <div className="shrink-0 inline-flex items-center gap-1">
                  <ActionButton
                    kind="include"
                    recommended={over}
                    selected={includeSelected && !elsewhere}
                    elsewhere={elsewhere && includeSelected}
                    onClick={() => onAddRule(i, { exclude: false })}
                    label={
                      includeSelected && !elsewhere
                        ? `Remove include: ${i.display_label} ${i.value_label}`
                        : elsewhere && includeSelected
                          ? `In another group: include ${i.display_label} ${i.value_label}`
                          : `Include ${i.display_label} ${i.value_label}`
                    }
                  />
                  <ActionButton
                    kind="exclude"
                    recommended={!over}
                    selected={excludeSelected && !elsewhere}
                    elsewhere={elsewhere && excludeSelected}
                    onClick={() => onAddRule(i, { exclude: true })}
                    label={
                      excludeSelected && !elsewhere
                        ? `Remove exclude: ${i.display_label} ${i.value_label}`
                        : elsewhere && excludeSelected
                          ? `In another group: exclude ${i.display_label} ${i.value_label}`
                          : `Exclude ${i.display_label} ${i.value_label}`
                    }
                  />
                </div>
              ) : null}
            </div>
            <div className="min-w-0">
              <div className={`text-2xl font-semibold leading-none tabular-nums ${r.tone}`}>{r.text}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wide text-gray-400">
                {i.share_conv === 0 ? 'no converters' : over ? 'more likely' : 'less likely'}
                {i.lift != null ? ` · ${i.lift > 0 ? '+' : ''}${i.lift.toFixed(0)} pts` : ''}
                {i.significant ? ' · ★' : i.significant === false ? ' · n.s.' : ''}
              </div>
            </div>
            <MiniBars shareConv={i.share_conv} shareBase={i.share_base} baselineLabel={baselineLabel} />
          </li>
        );
      })}
    </ol>
  );
}
